import React, { useMemo, useState, useCallback, useEffect } from "react";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { Maximize2, X, Shield, ChevronDown } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";

// Constants for dropdown options
const SEVERITY_OPTIONS = ["Critical", "High", "Medium", "Low"];
const STATUS_OPTIONS = ["New", "Open", "In Progress", "Resolved", "Closed"];

// MITRE ATT&CK Tactics in kill chain order
const MITRE_TACTICS = [
  { id: "reconnaissance", name: "Reconnaissance", short: "Recon" },
  { id: "resource-development", name: "Resource Development", short: "Resource Dev" },
  { id: "initial-access", name: "Initial Access", short: "Initial Access" },
  { id: "execution", name: "Execution", short: "Execution" },
  { id: "persistence", name: "Persistence", short: "Persistence" },
  { id: "privilege-escalation", name: "Privilege Escalation", short: "Priv Esc" },
  { id: "defense-evasion", name: "Defense Evasion", short: "Def Evasion" },
  { id: "credential-access", name: "Credential Access", short: "Cred Access" },
  { id: "discovery", name: "Discovery", short: "Discovery" },
  { id: "lateral-movement", name: "Lateral Movement", short: "Lateral Mov" },
  { id: "collection", name: "Collection", short: "Collection" },
  { id: "command-and-control", name: "Command and Control", short: "C2" },
  { id: "exfiltration", name: "Exfiltration", short: "Exfiltration" },
  { id: "impact", name: "Impact", short: "Impact" },
];

// Normalize tactic name for matching
const normalizeTactic = (name) => {
  if (!name) return "";
  return name
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .replace(/access$/, "access")
    .replace(/movement$/, "movement");
};

// Custom node for MITRE tactic stage - Small view (active only)
const MitreTacticNodeCompact = ({ data }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className="mitre-node mitre-node--active mitre-node--compact"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: "transparent", border: "none" }}
      />
      <div className="mitre-node__label">{data.label}</div>
      <div className="mitre-node__count">{data.count}</div>
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: "transparent", border: "none" }}
      />
      {showTooltip && (
        <div className="mitre-node__tooltip">
          <div className="mitre-node__tooltip-title">{data.fullName}</div>
          <div className="mitre-node__tooltip-count">{data.count} alert{data.count > 1 ? "s" : ""} detected</div>
          {data.alertNames && data.alertNames.length > 0 && (
            <div className="mitre-node__tooltip-alerts">
              {data.alertNames.slice(0, 3).map((name, i) => (
                <div key={i} className="mitre-node__tooltip-alert">• {name}</div>
              ))}
              {data.alertNames.length > 3 && (
                <div className="mitre-node__tooltip-more">+{data.alertNames.length - 3} more</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Custom node for MITRE tactic stage - Full view (all tactics)
const MitreTacticNodeFull = ({ data }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isActive = data.count > 0;

  return (
    <div
      className={`mitre-node ${isActive ? "mitre-node--active mitre-node--large" : "mitre-node--inactive"}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: "transparent", border: "none" }}
      />
      <div className="mitre-node__label">{data.label}</div>
      {isActive && (
        <div className="mitre-node__count">{data.count}</div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: "transparent", border: "none" }}
      />
      {showTooltip && isActive && (
        <div className="mitre-node__tooltip">
          <div className="mitre-node__tooltip-title">{data.fullName}</div>
          <div className="mitre-node__tooltip-count">{data.count} alert{data.count > 1 ? "s" : ""} detected</div>
          {data.alertNames && data.alertNames.length > 0 && (
            <div className="mitre-node__tooltip-alerts">
              {data.alertNames.slice(0, 5).map((name, i) => (
                <div key={i} className="mitre-node__tooltip-alert">• {name}</div>
              ))}
              {data.alertNames.length > 5 && (
                <div className="mitre-node__tooltip-more">+{data.alertNames.length - 5} more</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const mitreNodeTypesCompact = {
  mitreTactic: MitreTacticNodeCompact,
};

const mitreNodeTypesFull = {
  mitreTactic: MitreTacticNodeFull,
};

// Generate MITRE flow for compact view (only active tactics)
const generateMitreFlowCompact = (tacticCounts, alertsByTactic) => {
  const nodes = [];
  const edges = [];

  if (tacticCounts.length === 0) return { nodes, edges };

  const nodeWidth = 110;
  const nodeGap = 40;
  const startX = 30;
  const y = 40;

  // Only show active tactics
  const activeTactics = tacticCounts.filter((tc) => tc.value > 0);

  activeTactics.forEach((tc, index) => {
    const tacticId = normalizeTactic(tc.name);
    const alertNames = alertsByTactic.get(tacticId) || [];

    nodes.push({
      id: `compact-${tacticId}`,
      type: "mitreTactic",
      position: { x: startX + index * (nodeWidth + nodeGap), y },
      data: {
        label: tc.name,
        fullName: tc.name,
        count: tc.value,
        alertNames,
      },
    });

    // Connect to next active tactic
    if (index < activeTactics.length - 1) {
      const nextTacticId = normalizeTactic(activeTactics[index + 1].name);
      edges.push({
        id: `edge-compact-${tacticId}-${nextTacticId}`,
        source: `compact-${tacticId}`,
        target: `compact-${nextTacticId}`,
        type: "default",
        style: {
          stroke: "rgba(99, 102, 241, 0.5)",
          strokeWidth: 2,
          strokeDasharray: "6,4",
        },
      });
    }
  });

  return { nodes, edges };
};

// Generate MITRE flow for full view (all tactics)
const generateMitreFlowFull = (tacticCounts, alertsByTactic) => {
  const nodes = [];
  const edges = [];

  const nodeWidth = 100;
  const nodeGap = 30;
  const startX = 20;
  const y = 60;

  // Create a map for quick lookup
  const countMap = new Map();
  tacticCounts.forEach((tc) => {
    const normalized = normalizeTactic(tc.name);
    countMap.set(normalized, tc.value);
  });

  MITRE_TACTICS.forEach((tactic, index) => {
    const normalized = normalizeTactic(tactic.name);
    const count = countMap.get(normalized) || 0;
    const alertNames = alertsByTactic.get(normalized) || [];

    nodes.push({
      id: tactic.id,
      type: "mitreTactic",
      position: { x: startX + index * (nodeWidth + nodeGap), y },
      data: {
        label: tactic.short,
        fullName: tactic.name,
        count,
        alertNames,
      },
    });

    // Connect to next node
    if (index < MITRE_TACTICS.length - 1) {
      const nextCount = countMap.get(normalizeTactic(MITRE_TACTICS[index + 1].name)) || 0;
      const bothActive = count > 0 && nextCount > 0;
      
      edges.push({
        id: `edge-${tactic.id}-${MITRE_TACTICS[index + 1].id}`,
        source: tactic.id,
        target: MITRE_TACTICS[index + 1].id,
        type: "default",
        style: {
          stroke: bothActive ? "rgba(99, 102, 241, 0.6)" : "rgba(148, 163, 184, 0.3)",
          strokeWidth: bothActive ? 2.5 : 1.5,
          strokeDasharray: "6,4",
        },
      });
    }
  });

  return { nodes, edges };
};

const parsePossibleJson = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const coalesceNumber = (keys = [], sources = [], fallback = 0) => {
  for (const key of keys) {
    for (const source of sources) {
      if (!source || typeof source !== "object") continue;
      const value = source[key];
      if (value === 0 || value) {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
    }
  }
  return fallback;
};

const coalesceString = (keys = [], sources = [], fallback = "-") => {
  for (const key of keys) {
    for (const source of sources) {
      if (!source || typeof source !== "object") continue;
      const value = source[key];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }
  return fallback;
};

const parseEntities = (alerts = []) => {
  const collected = [];
  alerts.forEach((alert) => {
    const raw = alert.entities || alert.Entities;
    let list = raw;
    if (typeof raw === "string") {
      const parsed = parsePossibleJson(raw);
      list = parsed || [];
    }
    if (!Array.isArray(list)) return;
    list.forEach((entity) => {
      const host =
        entity.HostName ||
        entity.DeviceName ||
        entity.NetBiosName ||
        entity.Machine ||
        entity.Name;
      collected.push({
        host,
        type: entity.Type || entity.type || "",
        label:
          host ||
          entity.Address ||
          entity.IpAddress ||
          entity.IP ||
          entity.ip ||
          entity.ResourceId ||
          entity.AzureResourceId ||
          entity.Name ||
          "",
      });
    });
  });
  return collected;
};

const normalizeTeam = (source = {}) => {
  const candidates = [
    source.responseTeam,
    source.teamMembers,
    source.team,
    source.assignees,
    source.assignedUsers,
    source.owners,
    source.responders,
    source.responderList,
    source.teamMembersList,
    source.assignedTo,
  ];

  const parsedGroups = candidates
    .map((group) => {
      if (typeof group === "string") {
        const parsed = parsePossibleJson(group);
        if (parsed && Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === "object") return [parsed];
        return [group];
      }
      if (Array.isArray(group)) return group;
      if (group && typeof group === "object") return [group];
      return null;
    })
    .filter(Boolean)
    .flat();

  const members = parsedGroups
    .map((member, index) => {
      if (typeof member === "string") {
        const parsed = parsePossibleJson(member);
        if (parsed && typeof parsed === "object") {
          member = parsed;
        } else {
          const name = member.trim();
          if (!name) return null;
          return { name, role: null, id: `member-${index}` };
        }
      }

      if (member && typeof member === "object") {
        const name =
          member.name ||
          member.fullName ||
          member.displayName ||
          member.userName ||
          member.email ||
          member.userPrincipalName ||
          member.assignedTo ||
          null;
        const role = member.role || member.title || member.function || null;

        if (!name) {
          // drop placeholder objects with no identifying info
          return null;
        }

        return { name, role, id: member.id || member.userId || member.objectId || index };
      }

      return null;
    })
    .filter(Boolean);

  if (members.length) return members;

  const owner = source.owner || source.primaryOwner;
  if (owner) {
    if (typeof owner === "string") {
      return [{ name: owner, role: null, id: "owner" }];
    }
    if (typeof owner === "object") {
      const name =
        owner.name ||
        owner.fullName ||
        owner.displayName ||
        owner.userName ||
        owner.email ||
        owner.userPrincipalName ||
        "Owner";
      const role = owner.role || owner.title || null;
      return [{ name, role, id: owner.id || owner.userId || "owner" }];
    }
  }

  return [];
};

// MITRE Tactics Flow Component
const MitreTacticsFlow = ({ tacticCounts, alertsByTactic }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Compact view: only active tactics
  const { nodes: compactNodes, edges: compactEdges } = useMemo(() => {
    return generateMitreFlowCompact(tacticCounts, alertsByTactic);
  }, [tacticCounts, alertsByTactic]);

  // Full view: all tactics
  const { nodes: fullNodes, edges: fullEdges } = useMemo(() => {
    return generateMitreFlowFull(tacticCounts, alertsByTactic);
  }, [tacticCounts, alertsByTactic]);

  const activeTactics = tacticCounts.filter((t) => t.value > 0);

  const handleExpand = useCallback(() => setIsExpanded(true), []);
  const handleClose = useCallback(() => setIsExpanded(false), []);

  // Handle escape key
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isExpanded) {
        setIsExpanded(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isExpanded]);

  // If no active tactics, show message
  if (activeTactics.length === 0) {
    return (
      <div className="overview__section mitre-section">
        <div className="mitre-section__header">
          <h3 className="overview__section-title">
            <Shield size={16} />
            MITRE ATT&CK Kill Chain
          </h3>
        </div>
        <div className="mitre-section__empty">
          No tactics detected in alerts.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overview__section mitre-section">
        <div className="mitre-section__header">
          <h3 className="overview__section-title">
            <Shield size={16} />
            MITRE ATT&CK
          </h3>
          <button className="mitre-section__expand" onClick={handleExpand} title="View full kill chain">
            <Maximize2 size={16} />
          </button>
        </div>

        {/* Compact view - only show active tactics */}
        <div className="mitre-flow-container mitre-flow-container--compact">
          <ReactFlow
            nodes={compactNodes}
            edges={compactEdges}
            nodeTypes={mitreNodeTypesCompact}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.5}
            maxZoom={2}
            panOnDrag={false}
            zoomOnScroll={false}
            zoomOnPinch={false}
            preventScrolling={false}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="rgba(56, 189, 248, 0.05)" gap={20} />
          </ReactFlow>
        </div>

        <div className="mitre-section__hint">
          Hover over stages to see alert details. Click expand for full kill chain.
        </div>
      </div>

      {/* Fullscreen Modal - Full kill chain */}
      {isExpanded && (
        <div className="mitre-modal">
          <div className="mitre-modal__header">
            <h2 className="mitre-modal__title">
              <Shield size={20} />
              MITRE ATT&CK Kill Chain
            </h2>
            <button className="mitre-modal__close" onClick={handleClose}>
              <X size={20} />
            </button>
          </div>
          <div className="mitre-modal__body">
            <ReactFlow
              nodes={fullNodes}
              edges={fullEdges}
              nodeTypes={mitreNodeTypesFull}
              fitView
              fitViewOptions={{ padding: 0.15 }}
              minZoom={0.3}
              maxZoom={2}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="rgba(56, 189, 248, 0.05)" gap={25} />
              <Controls showInteractive={false} />
            </ReactFlow>
          </div>
          <div className="mitre-modal__footer">
            <div className="mitre-section__legend">
              <div className="mitre-section__legend-item">
                <div className="mitre-section__legend-dot mitre-section__legend-dot--active" />
                <span>Active tactic (detected)</span>
              </div>
              <div className="mitre-section__legend-item">
                <div className="mitre-section__legend-dot mitre-section__legend-dot--inactive" />
                <span>Not detected</span>
              </div>
            </div>
            {activeTactics.length > 0 && (
              <div className="mitre-section__summary">
                {activeTactics.map((t) => (
                  <span key={t.name} className="mitre-section__tag mitre-section__tag--large">
                    {t.name}: {t.value} alert{t.value > 1 ? "s" : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default function Overview({ incident, isEditing = false, editFormData = {}, onFormChange }) {
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Get current user to check if they are Helxon
  const { user } = useUser();
  const isHelxonUser = user?.publicMetadata?.client?.name === "Helxon";

  // Fetch assignable users when entering edit mode
  useEffect(() => {
    if (isEditing && assignableUsers.length === 0) {
      fetchAssignableUsers();
    }
  }, [isEditing]);

  const fetchAssignableUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.INCIDENT_ASSIGNABLE_USERS);
      setAssignableUsers(response.data || []);
    } catch (err) {
      console.error("Failed to fetch assignable users:", err);
      setAssignableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Filter users by client type (Helxon vs Customer)
  const helxonUsers = useMemo(() => {
    return assignableUsers.filter((u) => u.isHelxon === true);
  }, [assignableUsers]);

  const customerUsers = useMemo(() => {
    const clientUUID = incident?.clientUUID || incident?._raw?.clientUUID;
    // Filter non-Helxon users that belong to this incident's client
    return assignableUsers.filter(
      (u) => !u.isHelxon && (u.clientUUID === clientUUID || !clientUUID)
    );
  }, [assignableUsers, incident]);

  // Convert AssignableUser to AssignedUser format for API
  const toAssignedUser = (user) => {
    if (!user) return null;
    return {
      objectId: user.objectId,
      email: user.email || "",
      userPrincipalName: user.userPrincipalName || "",
      displayName: user.displayName || user.name || null,
    };
  };

  const handleSelectChange = (field, value) => {
    if (onFormChange) {
      // For user selection, convert to AssignedUser format
      if (field === "helxonAssignedTo" || field === "customerAssignedTo") {
        if (!value) {
          onFormChange(field, null);
        } else {
          const userList = field === "helxonAssignedTo" ? helxonUsers : customerUsers;
          const selectedUser = userList.find((u) => u.objectId === value || u.id === value);
          onFormChange(field, toAssignedUser(selectedUser));
        }
      } else {
        onFormChange(field, value);
      }
    }
  };

  // Helper to get display name for assigned user
  const getAssigneeDisplay = (assignee) => {
    if (!assignee) return "—";
    return assignee.displayName || assignee.userPrincipalName || assignee.email || "—";
  };
  const baseSource = incident?._raw ?? incident ?? {};
  const alerts = incident?.alerts || baseSource?.alerts || [];
  const parsedAdditional =
    parsePossibleJson(baseSource.additionalData) ||
    parsePossibleJson(baseSource._raw?.additionalData) ||
    null;

  const source = useMemo(() => {
    if (!parsedAdditional || typeof parsedAdditional !== "object") {
      return baseSource;
    }
    // shallow merge so additionalData fields become directly accessible
    return { ...baseSource, ...parsedAdditional };
  }, [baseSource, parsedAdditional]);

  const extraSlices = useMemo(() => {
    const slices = [];
    if (parsedAdditional && typeof parsedAdditional === "object") slices.push(parsedAdditional);
    if (parsedAdditional?.stats) slices.push(parsedAdditional.stats);
    if (parsedAdditional?.counts) slices.push(parsedAdditional.counts);
    if (parsedAdditional?.metrics) slices.push(parsedAdditional.metrics);
    if (parsedAdditional?.details) slices.push(parsedAdditional.details);
    return slices;
  }, [parsedAdditional]);

  const sourcesForLookup = useMemo(
    () => [source, baseSource, incident, ...extraSlices].filter(Boolean),
    [source, baseSource, incident, extraSlices]
  );

  const metrics = useMemo(() => {
    const entities = parseEntities(alerts);
    const uniqueHosts = new Set(
      entities
        .map((e) => (e.host && e.host.toLowerCase()) || null)
        .filter(Boolean)
    ).size;

    const indicatorsFound = coalesceNumber(
      [
        "indicatorsFound",
        "indicatorsCount",
        "indicatorCount",
        "iocCount",
        "iocDetected",
      ],
      sourcesForLookup,
      0
    );

    const dataExfiltrated = coalesceString(
      [
        "dataExfiltrated",
        "dataExfiltration",
        "dataExfiltratedAmount",
        "dataLoss",
        "exfiltrationSize",
        "dataExfiltrationSize",
        "dataExfiltrationVolume",
      ],
      sourcesForLookup,
      "-"
    );

    const alertCount = coalesceNumber(
      ["alertsCount", "AlertsCount"],
      sourcesForLookup,
      alerts?.length || 0
    );

    const workspaces = new Set(
      alerts
        .map((a) => a.workspaceName || a.workspaceId || a.WorkspaceId || a.WorkspaceName)
        .filter(Boolean)
    );

    return {
      uniqueHosts,
      indicatorsFound,
      dataExfiltrated,
      alertCount,
      workspaces: Array.from(workspaces),
    };
  }, [alerts, sourcesForLookup]);

  const tactics =
    parsedAdditional?.tactics ||
    parsedAdditional?.Tactics ||
    source.tactics ||
    source.Tactics ||
    [];
  const techniques =
    parsedAdditional?.techniques ||
    parsedAdditional?.Techniques ||
    source.techniques ||
    source.Techniques ||
    [];
  const providerIncidentUrl = null;

  const teamMembers = useMemo(() => normalizeTeam(source), [source]);

  const owner =
    typeof source.owner === "string"
      ? parsePossibleJson(source.owner) || source.owner
      : source.owner;
  const ownerDisplay =
    owner && typeof owner === "object"
      ? owner.userPrincipalName ||
        owner.email ||
        owner.assignedTo ||
        owner.name ||
        null
      : owner;

  const parseArray = (val) => {
    if (!val && val !== 0) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      const parsed = parsePossibleJson(val);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") return Object.values(parsed);
      return [];
    }
    if (typeof val === "object") return Object.values(val);
    return [];
  };

  const incidentDetails = {
    tenantId: coalesceString(["tenantId", "TenantId"], sourcesForLookup, "-"),
    sourceSystem: coalesceString(["sourceSystem", "SourceSystem"], sourcesForLookup, "-"),
    title: coalesceString(["title", "Title"], sourcesForLookup, "-"),
    description: coalesceString(["description", "Description"], sourcesForLookup, "-"),
    severity: coalesceString(["severity", "Severity"], sourcesForLookup, "-"),
    status: coalesceString(["status", "Status"], sourcesForLookup, "-"),
    providerName: coalesceString(["providerName", "ProviderName"], sourcesForLookup, "-"),
    providerIncidentId: coalesceString(
      ["providerIncidentId", "ProviderIncidentId"],
      sourcesForLookup,
      "-"
    ),
    incidentNumber: coalesceString(["incidentNumber", "IncidentNumber"], sourcesForLookup, "-"),
    clientUUID: coalesceString(["clientUUID", "ClientUUID"], sourcesForLookup, "-"),
    modifiedBy: coalesceString(["modifiedBy", "ModifiedBy"], sourcesForLookup, "-"),
    type: coalesceString(["type", "Type"], sourcesForLookup, "-"),
  };

  const alertIds = parseArray(
    coalesceString(["alertIds", "AlertIds"], sourcesForLookup, null) || source.alertIds
  );
  const relatedRuleIds = parseArray(
    coalesceString(["relatedAnalyticRuleIds", "RelatedAnalyticRuleIds"], sourcesForLookup, null)
  );
  const bookmarkIds = parseArray(
    coalesceString(["bookmarkIds", "BookmarkIds"], sourcesForLookup, null)
  );
  const comments = parseArray(coalesceString(["comments", "Comments"], sourcesForLookup, null));
  const tasks = parseArray(coalesceString(["tasks", "Tasks"], sourcesForLookup, null));
  const labels = parseArray(coalesceString(["labels", "Labels"], sourcesForLookup, null));

  const classification = {
    classification: coalesceString(["classification", "Classification"], sourcesForLookup, "-"),
    reason: coalesceString(
      ["classificationReason", "ClassificationReason"],
      sourcesForLookup,
      "-"
    ),
    comment: coalesceString(
      ["classificationComment", "ClassificationComment"],
      sourcesForLookup,
      "-"
    ),
  };

  const timeFields = [
    { label: "Created", value: coalesceString(["createdTime", "CreatedAt", "createdAt"], sourcesForLookup, null) },
    { label: "Updated", value: coalesceString(["updatedAt", "UpdatedAt", "lastModifiedTime", "LastModifiedTime"], sourcesForLookup, null) },
    { label: "First Activity", value: coalesceString(["firstActivityTime", "FirstActivityTime"], sourcesForLookup, null) },
    { label: "Last Activity", value: coalesceString(["lastActivityTime", "LastActivityTime"], sourcesForLookup, null) },
    { label: "Time Generated", value: coalesceString(["timeGenerated", "TimeGenerated"], sourcesForLookup, null) },
    { label: "Closed", value: coalesceString(["closedTime", "ClosedTime"], sourcesForLookup, null) },
    { label: "First Modified", value: coalesceString(["firstModifiedTime", "FirstModifiedTime"], sourcesForLookup, null) },
  ];

  const formatDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toLocaleString();
  };

  const { tacticCounts, alertsByTactic } = useMemo(() => {
    const countMap = new Map();
    const alertNamesMap = new Map(); // Map tactic to array of alert names
    
    alerts.forEach((alert) => {
      const alertName = alert.displayName || alert.alertName || alert.name || "Unknown Alert";
      const tact = alert.tactics || alert.Tactics;
      let list = tact;
      if (typeof tact === "string") {
        const parsed = parsePossibleJson(tact);
        list = parsed || tact.split(/[;,]/).map((t) => t.trim());
      }
      if (Array.isArray(list)) {
        list.forEach((t) => {
          if (!t) return;
          const key = t.toString();
          const normalizedKey = normalizeTactic(key);
          countMap.set(key, (countMap.get(key) || 0) + 1);
          
          // Track alert names for this tactic
          if (!alertNamesMap.has(normalizedKey)) {
            alertNamesMap.set(normalizedKey, []);
          }
          alertNamesMap.get(normalizedKey).push(alertName);
        });
      } else if (list) {
        const key = list.toString();
        const normalizedKey = normalizeTactic(key);
        countMap.set(key, (countMap.get(key) || 0) + 1);
        
        if (!alertNamesMap.has(normalizedKey)) {
          alertNamesMap.set(normalizedKey, []);
        }
        alertNamesMap.get(normalizedKey).push(alertName);
      }
    });
    
    const counts = Array.from(countMap.entries()).map(([name, value], idx) => ({
      name,
      value,
      step: idx + 1,
    }));
    
    return { tacticCounts: counts, alertsByTactic: alertNamesMap };
  }, [alerts]);

  return (
    <div className="overview">
      {/* Wrapper for overview__top and Incident Details */}
      <div className="overview__top-wrapper">
        <div className="overview__top">
          {/* Incident Metrics */}
          <div className="overview__section">
            <h3 className="overview__section-title">Incident Metrics</h3>
            <div className="metrics-list">
              <div className="metric-item">
                <span className="metric-item__label">Unique Hosts:</span>
                <span className="metric-item__value">{metrics.uniqueHosts}</span>
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Alerts:</span>
                <span className="metric-item__value">{metrics.alertCount}</span>
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Affected Workspaces:</span>
                <span className="metric-item__value">
                  {metrics.workspaces.length ? metrics.workspaces.join(", ") : "—"}
                </span>
              </div>
              <div className="metric-item metric-item--critical">
                <span className="metric-item__label">Data Exfiltrated:</span>
                <span className="metric-item__value">{metrics.dataExfiltrated}</span>
              </div>
            </div>
          </div>

          {/* MITRE ATT&CK */}
          <MitreTacticsFlow tacticCounts={tacticCounts} alertsByTactic={alertsByTactic} />
        </div>

        {/* Incident Details and Timestamps side by side */}
        <div className="overview__details-row">
          {/* Incident Details */}
          <div className="overview__section">
            <h3 className="overview__section-title">Incident Details</h3>
            <div className="metrics-list">
              <div className="metric-item">
                <span className="metric-item__label">Title:</span>
                <span className="metric-item__value">{incidentDetails.title}</span>
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Severity:</span>
                {isEditing && isHelxonUser ? (
                  <div className="metric-item__select-wrapper">
                    <select
                      className="metric-item__select"
                      value={editFormData.severity || ""}
                      onChange={(e) => handleSelectChange("severity", e.target.value)}
                    >
                      <option value="">— Select Severity —</option>
                      {SEVERITY_OPTIONS.map((sev) => (
                        <option key={sev} value={sev}>
                          {sev}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="metric-item__select-icon" />
                  </div>
                ) : (
                  <span className="metric-item__value">{incidentDetails.severity}</span>
                )}
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Status:</span>
                {isEditing && isHelxonUser ? (
                  <div className="metric-item__select-wrapper">
                    <select
                      className="metric-item__select"
                      value={editFormData.status || ""}
                      onChange={(e) => handleSelectChange("status", e.target.value)}
                    >
                      <option value="">— Select Status —</option>
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="metric-item__select-icon" />
                  </div>
                ) : (
                  <span className="metric-item__value">{incidentDetails.status}</span>
                )}
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Provider:</span>
                <span className="metric-item__value">
                  {incidentDetails.providerName} ({incidentDetails.providerIncidentId})
                </span>
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Source System:</span>
                <span className="metric-item__value">{incidentDetails.sourceSystem}</span>
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Incident #:</span>
                <span className="metric-item__value">{incidentDetails.incidentNumber}</span>
              </div>
              <div className="metric-item">
                <span className="metric-item__label">Customer:</span>
                <span className="metric-item__value">
                  {source.clientName ||
                    source.ClientName ||
                    incidentDetails.clientUUID ||
                    "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="overview__section">
            <h3 className="overview__section-title">Timestamps</h3>
            <div className="metrics-list">
              {timeFields.map((t) => (
                <div key={t.label} className="metric-item">
                  <span className="metric-item__label">{t.label}:</span>
                  <span className="metric-item__value">{formatDate(t.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Left Column */}
      <div className="overview__left">

        {/* Response Team */}
        <div className="overview__section">
          <h3 className="overview__section-title">Response Team</h3>
          <div className="team-list">
            <div className="team-member">
              <div className="team-member__name">Helxon</div>
              <div className="team-member__role">
                <span className="team-member__label">Assign To:</span>
                {isEditing && isHelxonUser ? (
                  <div className="team-member__select-wrapper">
                    <select
                      className="team-member__select"
                      value={editFormData.helxonAssignedTo?.objectId || ""}
                      onChange={(e) => handleSelectChange("helxonAssignedTo", e.target.value)}
                      disabled={loadingUsers}
                    >
                      <option value="">— Select Assignee —</option>
                      {helxonUsers.map((u) => (
                        <option key={u.objectId} value={u.objectId}>
                          {u.displayName || u.userPrincipalName || u.email || u.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="team-member__select-icon" />
                  </div>
                ) : (
                  <span className="team-member__value">
                    {getAssigneeDisplay(incident?.helxonAssignedTo || incident?._raw?.helxonAssignedTo)}
                  </span>
                )}
              </div>
            </div>
            <div className="team-member">
              <div className="team-member__name">Customer</div>
              <div className="team-member__role">
                <span className="team-member__label">Assign To:</span>
                {isEditing ? (
                  <div className="team-member__select-wrapper">
                    <select
                      className="team-member__select"
                      value={editFormData.customerAssignedTo?.objectId || ""}
                      onChange={(e) => handleSelectChange("customerAssignedTo", e.target.value)}
                      disabled={loadingUsers}
                    >
                      <option value="">— Select Assignee —</option>
                      {customerUsers.map((u) => (
                        <option key={u.objectId} value={u.objectId}>
                          {u.displayName || u.userPrincipalName || u.email || u.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="team-member__select-icon" />
                  </div>
                ) : (
                  <span className="team-member__value">
                    {getAssigneeDisplay(incident?.customerAssignedTo || incident?._raw?.customerAssignedTo)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tactics / Techniques */}
        <div className="overview__section">
          <h3 className="overview__section-title">Detection Context</h3>
          <div className="metrics-list">
            <div className="metric-item">
              <span className="metric-item__label">Tactics:</span>
              <span className="metric-item__value">
                {Array.isArray(tactics) && tactics.length
                  ? tactics.join(", ")
                  : "—"}
              </span>
            </div>
            <div className="metric-item">
              <span className="metric-item__label">Techniques:</span>
              <span className="metric-item__value">
                {Array.isArray(techniques) && techniques.length
                  ? techniques.join(", ")
                  : "—"}
              </span>
            </div>
            {providerIncidentUrl && (
              <div className="metric-item">
                <span className="metric-item__label">Source Incident:</span>
                <a
                  className="metric-item__value"
                  href={providerIncidentUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Provider
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Classification */}
        <div className="overview__section">
          <h3 className="overview__section-title">Classification</h3>
          <div className="metrics-list">
            <div className="metric-item">
              <span className="metric-item__label">Classification:</span>
              <span className="metric-item__value">{classification.classification}</span>
            </div>
            <div className="metric-item">
              <span className="metric-item__label">Reason:</span>
              <span className="metric-item__value">{classification.reason}</span>
            </div>
            <div className="metric-item">
              <span className="metric-item__label">Comment:</span>
              <span className="metric-item__value">{classification.comment}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
